import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Row, Spin } from 'antd';
import { EmptyState } from '../empty_state/EmptyState';
import SupplierPerformanceToolbar from './SupplierPerformanceToolbar';
import AnalyticsControlBar from '../analytics_control_bar/AnalyticsControlBar';
import SupplierPerformanceScatterSection from './SupplierPerformanceScatterSection';
import SupplierPerformanceSuppliersPanel from './SupplierPerformanceSuppliersPanel';
import './SupplierPerformanceTab.less';

const EMPTY_STATE_GRAPHIC_URL =
	'https://s3.amazonaws.com/mock-data-assets/categories/images/sloth.svg';

const SupplierPerformanceTab = ({
	spinning,
	error,
	hasNoSuppliersInNetwork,
	hasNoFilteredSuppliers,
	navigateToFindVendors,
	clearAllFilters,
	toolbar,
	userType,
	filterValues,
	updateFilters,
	activeFilterCount,
	scatter,
	panel,
	hoveredSupplierId,
	onHoverSupplier,
}) => {
	const { suppliersPanelOpen } = toolbar;
	const rowRef = useRef(null);
	const chartInstanceRef = useRef(null);
	const [paneHeight, setPaneHeight] = useState(520);
	const [tableHeaderHeight, setTableHeaderHeight] = useState(0);
	const suppressChartHoverRef = useRef(false);

	const handleGetChartInstance = (chart) => {
		chartInstanceRef.current = chart;
		if (!chart) {
			return;
		}
		const syncHoveredSupplier = (evt) => {
			if (suppressChartHoverRef.current) {
				return;
			}
			const items = evt && evt.data && evt.data.items;
			const datum = items && items.length ? items[0].data : null;
			if (datum && datum.supplierFacilityId !== null && datum.supplierFacilityId !== undefined) {
				onHoverSupplier(datum.supplierFacilityId);
			}
		};
		chart.on('tooltip:show', syncHoveredSupplier);
		chart.on('tooltip:change', syncHoveredSupplier);
		chart.on('tooltip:hide', () => {
			if (suppressChartHoverRef.current) {
				return;
			}
			onHoverSupplier(null);
		});
	};
	const highlightSupplierOnChart = (record) => {
		if (!record || !record.hasChartMetrics) {
			return;
		}
		onHoverSupplier(record.supplierFacilityId);
		const chart = chartInstanceRef.current;
		if (!chart) {
			return;
		}
		try {
			const point = chart.getXY(record);
			if (point) {
				suppressChartHoverRef.current = true;
				chart.showTooltip(point);
			}
		} catch (error) {
			// Chart not ready or record not plottable — nothing to highlight.
		}
	};
	const clearChartHighlight = () => {
		suppressChartHoverRef.current = false;
		onHoverSupplier(null);
		const chart = chartInstanceRef.current;
		if (!chart) {
			return;
		}
		try {
			chart.hideTooltip();
		} catch (error) {
			// no-op
		}
	};

	useEffect(() => {
		const compute = () => {
			const node = rowRef.current;
			if (!node) {
				return;
			}
			const top = node.getBoundingClientRect().top;
			setPaneHeight(Math.max(360, Math.floor(window.innerHeight - top - 24)));
		};
		compute();
		window.addEventListener('resize', compute);
		return () => window.removeEventListener('resize', compute);
	}, [
		suppliersPanelOpen,
		spinning,
		activeFilterCount,
		hasNoSuppliersInNetwork,
		hasNoFilteredSuppliers,
	]);

	useEffect(() => {
		const node = rowRef.current;
		if (!node) {
			return;
		}
		const thead = node.querySelector('.supplierPerformancePanelCard .ant-table-thead');
		setTableHeaderHeight(thead ? Math.ceil(thead.getBoundingClientRect().height) : 0);
	}, [suppliersPanelOpen, spinning, paneHeight, activeFilterCount]);

	useEffect(() => {
		const chart = chartInstanceRef.current;
		if (!chart) {
			return undefined;
		}
		const frame = requestAnimationFrame(() => {
			try {
				chart.forceFit();
			} catch (error) {
				// chart not ready
			}
		});
		return () => cancelAnimationFrame(frame);
	}, [suppliersPanelOpen, spinning]);

	let content = null;
	if (error) {
		content = <Alert className="mt-4" type="error" message={error} />;
	} else if (hasNoSuppliersInNetwork) {
		content = (
			<EmptyState
				graphic={<img style={{ marginBottom: 8 }} src={EMPTY_STATE_GRAPHIC_URL} />}
				headline={'My work here is done'}
				body={
					<div style={{ textAlign: 'center' }}>
						<div style={{ maxWidth: 440, marginBottom: 16 }}>
							You haven't added any suppliers to your private network yet. Once you've added them,
							we'll start keeping track of their performance here.
						</div>
						<Button type="primary" onClick={navigateToFindVendors}>
							Find vendors
						</Button>
					</div>
				}
			/>
		);
	} else if (hasNoFilteredSuppliers) {
		content = (
			<EmptyState
				graphic={<img style={{ marginBottom: 8 }} src={EMPTY_STATE_GRAPHIC_URL} />}
				headline={`Couldn't find any suppliers matching these filters`}
				body={
					<div style={{ textAlign: 'center' }}>
						<div style={{ maxWidth: 440, marginBottom: 16 }}>
							Change or clear your filters to see your suppliers here
						</div>
						<Button onClick={clearAllFilters}>Clear Filters</Button>
					</div>
				}
			/>
		);
	} else {
		content = (
			<div ref={rowRef} className="mt-4">
				<Row gutter={12}>
					<Col span={suppliersPanelOpen ? 12 : 24}>
						<Card
							className="overflow-hidden [&_.ant-card-body]:!p-0"
							style={{ height: paneHeight }}
						>
							<SupplierPerformanceScatterSection
								{...scatter}
								chartHeight={paneHeight}
								onGetG2Instance={handleGetChartInstance}
							/>
						</Card>
					</Col>
					{suppliersPanelOpen ? (
						<Col span={12}>
							<Card
								className="supplierPerformancePanelCard [&_.ant-card-body]:!p-0"
								style={{ height: paneHeight }}
							>
								<SupplierPerformanceSuppliersPanel
									{...panel}
									scrollY={Math.max(160, paneHeight - (tableHeaderHeight || 56) - 4)}
									onRowHover={highlightSupplierOnChart}
									onRowLeave={clearChartHighlight}
									highlightedSupplierId={hoveredSupplierId}
								/>
							</Card>
						</Col>
					) : null}
				</Row>
			</div>
		);
	}

	return (
		<div className="supplierPerformanceTab mt-4">
			<AnalyticsControlBar
				userType={userType}
				updateFilters={updateFilters}
				startDate={filterValues.startDate}
				endDate={filterValues.endDate}
				dateRangeLabel="Completed Between"
				locationIds={filterValues.locationIds}
				supplierFacilityIds={filterValues.supplierFacilityIds}
				problemTypeIds={filterValues.problemTypeIds}
				woPriorityIds={filterValues.woPriorityIds}
				isPM={filterValues.isPM}
				workCreatedAtStartDate={filterValues.workCreatedAtStartDate}
				workCreatedAtEndDate={filterValues.workCreatedAtEndDate}
				filterConfig={{
					problemTypes: true,
					workOrderPriorities: true,
					isPM: true,
					workCreatedAt: true,
					isSupplierAnalytics: true,
				}}
				hideLocationsControl={false}
				hideSuppliersControl={false}
				showSearch={false}
				showDownload={false}
				showPrint={false}
				additionalConfigs={<SupplierPerformanceToolbar {...toolbar} />}
				filterWrapperClassname="flex-1"
			/>
			<Spin spinning={spinning}>{content}</Spin>
		</div>
	);
};

export default SupplierPerformanceTab;
